import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Paper,
  Chip
} from '@mui/material';
import { Phone, Email, LocationOn, Business, Support } from '@mui/icons-material';

const ContactUs = () => {
  const contactMethods = [
    {
      icon: <Phone sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Phone Support',
      description: 'Speak directly with our experts',
      details: '+1 (312) 376-3265',
      availability: '24/7 Available'
    },
    {
      icon: <Email sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Email Support',
      description: 'Get detailed responses via email',
      details: 'contact@sicherstellentechnologie.com',
      availability: 'Response within 24 hours'
    },
    {
      icon: <Support sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Live Chat',
      description: 'Instant support when you need it',
      details: 'Available now',
      availability: 'Mon-Fri 9AM-6PM'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 10,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 3
            }}
          >
            Contact Us
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            Get in touch with our team. We're here to help you transform your business with cutting-edge technology solutions.
          </Typography>
        </Container>
      </Box>

      <Box sx={{ py: 8, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          {/* Contact Methods */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h3"
              sx={{
                textAlign: 'center',
                fontWeight: 'bold',
                color: '#1a1a1a',
                mb: 6
              }}
            >
              How Can We Help You?
            </Typography>
            <Grid container spacing={4}>
              {contactMethods.map((method, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Box sx={{ mb: 3 }}>
                        {method.icon}
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 'bold',
                          color: '#1a1a1a',
                          mb: 2
                        }}
                      >
                        {method.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#666',
                          mb: 2
                        }}
                      >
                        {method.description}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#1976d2',
                          fontWeight: 'bold',
                          mb: 1,
                          fontSize: '0.9rem'
                        }}
                      >
                        {method.details}
                      </Typography>
                      <Chip
                        label={method.availability}
                        size="small"
                        sx={{
                          bgcolor: '#e3f2fd',
                          color: '#1976d2',
                          fontWeight: 'bold'
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

        <Grid container spacing={6}>
          {/* Contact Information */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 4, height: '100%', bgcolor: 'white' }}>
              <Typography variant="h4" sx={{ color: '#167d7f', mb: 4, fontWeight: 'bold' }}>
                Get in Touch
              </Typography>
              
              <Stack spacing={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Phone sx={{ color: '#167d7f', mr: 2 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Phone
                    </Typography>
                    <Typography color="text.secondary">
                      +1 (312) 376-3265
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Email sx={{ color: '#167d7f', mr: 2 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Email
                    </Typography>
                    <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                    contact@sicherstellentechnologie.com
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOn sx={{ color: '#167d7f', mr: 2 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Address
                    </Typography>
                    <Typography color="text.secondary">
                    2828 N Clark St 3rd Floor<br />
                    Chicago, IL 60453
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Business sx={{ color: '#167d7f', mr: 2 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Business Hours
                    </Typography>
                    <Typography color="text.secondary">
                      Mon - Fri: 9:00 AM - 6:00 PM<br />
                      Sat: 10:00 AM - 4:00 PM
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={8}>
            <Card elevation={0} sx={{ bgcolor: 'white' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" sx={{ color: '#167d7f', mb: 4, fontWeight: 'bold' }}>
                  Send us a Message
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#167d7f',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#167d7f',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#167d7f',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#167d7f',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#167d7f',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#167d7f',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#167d7f',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#167d7f',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Company"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#167d7f',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#167d7f',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subject"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#167d7f',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#167d7f',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      multiline
                      rows={4}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#167d7f',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#167d7f',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        bgcolor: '#167d7f',
                        '&:hover': { bgcolor: '#0f5f61' },
                        px: 6,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold'
                      }}
                    >
                      Send Message
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Additional Information Section */}
        <Box sx={{ mt: 8 }}>
          <Typography variant="h4" sx={{ color: '#167d7f', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Why Choose Us?
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'white' }}>
                <Typography variant="h5" sx={{ color: '#167d7f', mb: 2, fontWeight: 'bold' }}>
                  Expert Team
                </Typography>
                <Typography color="text.secondary">
                  Our experienced professionals bring years of industry expertise to every project.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'white' }}>
                <Typography variant="h5" sx={{ color: '#167d7f', mb: 2, fontWeight: 'bold' }}>
                  Innovative Solutions
                </Typography>
                <Typography color="text.secondary">
                  We leverage cutting-edge technology to deliver solutions that drive real business value.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: 'white' }}>
                <Typography variant="h5" sx={{ color: '#167d7f', mb: 2, fontWeight: 'bold' }}>
                  24/7 Support
                </Typography>
                <Typography color="text.secondary">
                  Round-the-clock support ensures your systems run smoothly and efficiently.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default ContactUs;
